import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface StorySectionProps {
  question: string;
  icon: string;
  children: ReactNode;
  diveDeepLink?: string;
  diveDeepLabel?: string;
  defaultExpanded?: boolean;
}

export function StorySection({
  question,
  icon,
  children,
  diveDeepLink,
  diveDeepLabel = 'Dive Deeper',
  defaultExpanded = true,
}: StorySectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <section className="card p-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left group"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h3 className="text-lg font-semibold text-white group-hover:text-primary-300 transition-colors">
            {question}
          </h3>
        </div>
        <svg
          className={`w-5 h-5 text-text-secondary transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {children}

          {diveDeepLink && (
            <Link
              to={diveDeepLink}
              className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              {diveDeepLabel}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: number;
  trendLabel?: string;
  icon?: string;
}

export function MetricCard({ label, value, trend, trendLabel, icon }: MetricCardProps) {
  return (
    <div className="bg-surface-700/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className="text-lg">{icon}</span>}
        <span className="text-xs text-text-tertiary uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {trend !== undefined && (
        <div className={`text-sm ${trend >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% {trendLabel}
        </div>
      )}
    </div>
  );
}

interface InsightSource {
  name: string;
  type: 'nielsen' | 'kantar' | 'internal' | 'survey' | 'synthetic' | 'estimate';
  date?: string;
  confidence?: number; // 0-1
}

interface InsightCardProps {
  text: string;
  type?: 'info' | 'success' | 'warning' | 'danger';
  sources?: InsightSource[];
  language?: string;
}

export function InsightCard({ text, type = 'info', sources, language = 'en' }: InsightCardProps) {
  const t = language === 'es';
  const styles = {
    info: 'border-l-primary-500 bg-primary-500/5',
    success: 'border-l-success-500 bg-success-500/5',
    warning: 'border-l-warning-500 bg-warning-500/5',
    danger: 'border-l-danger-500 bg-danger-500/5',
  };

  const sourceTypeLabels: Record<InsightSource['type'], { en: string; es: string; icon: string }> = {
    nielsen: { en: 'Nielsen', es: 'Nielsen', icon: '📊' },
    kantar: { en: 'Kantar', es: 'Kantar', icon: '📈' },
    internal: { en: 'Internal Data', es: 'Datos Internos', icon: '🏢' },
    survey: { en: 'Survey', es: 'Encuesta', icon: '📋' },
    synthetic: { en: 'Synthetic', es: 'Sintético', icon: '🤖' },
    estimate: { en: 'Estimate', es: 'Estimación', icon: '📐' },
  };

  return (
    <div className={`border-l-4 ${styles[type]} rounded-r-lg p-4`}>
      <p className="text-sm text-text-secondary">{text}</p>
      {sources && sources.length > 0 && (
        <div className="mt-2 pt-2 border-t border-surface-600/50">
          <div className="flex items-center gap-1 text-xs text-text-tertiary">
            <span>{t ? 'Fuentes:' : 'Sources:'}</span>
            {sources.map((source, idx) => {
              const label = sourceTypeLabels[source.type];
              return (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-surface-600/50 rounded"
                  title={`${label.icon} ${t ? label.es : label.en}${source.date ? ` (${source.date})` : ''}${source.confidence ? ` - ${(source.confidence * 100).toFixed(0)}% ${t ? 'confianza' : 'confidence'}` : ''}`}
                >
                  <span>{label.icon}</span>
                  <span>{source.name || (t ? label.es : label.en)}</span>
                  {source.confidence && source.confidence < 0.8 && (
                    <span className="text-warning-400">⚠</span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
